import { HTMLAttributes, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Stripe, loadStripe } from '@stripe/stripe-js';
import { FieldValues, FormState } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { InlineSpinner } from '../spinner';

import { User, useUser } from '../../../hooks/api/useUser';
import { useStripeKeys, cardBrandType } from '../../../hooks/api/useStripe';

import { CardIcon } from './card-icon';
import useAuth from '../../../hooks/utils/useAuth';

export interface AddPaymentFormProps extends HTMLAttributes<HTMLFormElement> {
  topActionButton?: (formState: Partial<FormState<FieldValues>>) => ReactNode;
  bottomActionButton?: (formState: Partial<FormState<FieldValues>>) => ReactNode;
  onSubmitPayment?: (stripePaymentMethod: string) => Promise<unknown>;
  onSuccessAddPayment?: () => void;
  onErrorAddPayment?: () => void;
  user?: User | null;
}

const AddPaymentFormWithStripe: React.FC<AddPaymentFormProps> = ({ className, ...props }) => {
  const { user, isLoading: userIsLoading } = useAuth();
  const { data: { publishable_key } = {}, isLoading: isStripeKeysLoading } = useStripeKeys(user?.caller_role_id);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    if (publishable_key) {
      setStripePromise(loadStripe(publishable_key));
    }
  }, [publishable_key]);

  if (userIsLoading || !stripePromise || isStripeKeysLoading) {
    return (
      <div className={twMerge('flex h-full w-full items-center justify-center', className)}>
        <InlineSpinner />
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <AddPaymentElements className={className} {...props} user={user} />
    </Elements>
  );
};

const AddPaymentElements: React.FC<AddPaymentFormProps> = ({
  className,
  topActionButton,
  bottomActionButton,
  onSubmitPayment,
  onSuccessAddPayment,
  onErrorAddPayment,
  user,
  ...props
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { updateUser } = useUser();

  const stripe = useStripe();
  const elements = useElements();

  const { data: { intent_client_secret } = {} } = useStripeKeys(user?.caller_role_id);

  const cardIconsList = useMemo<Record<'brandName', cardBrandType>[]>(
    () => [
      {
        brandName: 'visa',
      },
      {
        brandName: 'mastercard',
      },
      {
        brandName: 'amex',
      },
    ],
    [],
  );

  const ELEMENT_OPTIONS = useMemo(
    () => ({
      classes: {
        base: 'w-full rounded-[12px] border border-input-border-gray bg-gray-50 px-3 py-4',
        focus: 'outline-none ring-2 ring-button-primary border-transparent',
        invalid: '!border-red',
      },
      style: {
        base: {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '16px',
          color: 'rgb(17, 24, 39)',
          '::placeholder': {
            color: '#9CA3AF',
          },
        },
        invalid: {
          color: '#EF4444',
        },
      },
    }),
    [],
  );

  const { topActionButtonElement, bottomActionButtonElement } = useMemo(
    () => ({
      topActionButtonElement: topActionButton ? topActionButton({ isSubmitting }) : null,
      bottomActionButtonElement: bottomActionButton ? bottomActionButton({ isSubmitting }) : null,
    }),
    [topActionButton, bottomActionButton, isSubmitting],
  );

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    setIsSubmitting(true);

    if (elements && stripe && intent_client_secret) {
      const cardNumberElement = elements.getElement(CardNumberElement);

      if (cardNumberElement) {
        const cardSetupResponse = await stripe.confirmCardSetup(intent_client_secret, {
          payment_method: {
            card: cardNumberElement,
          },
        });

        if (typeof cardSetupResponse?.setupIntent?.payment_method === 'string') {
          if (onSubmitPayment) {
            await onSubmitPayment(cardSetupResponse.setupIntent.payment_method);
          } else {
            // update the user record with the stripe payment method ID
            await updateUser.mutateAsync({
              caller_role: {
                ...user?.caller_role!,
                stripe_payment_method_id: cardSetupResponse.setupIntent.payment_method,
              },
            });
          }

          if (onSuccessAddPayment) {
            onSuccessAddPayment();
          }
        } else if (cardSetupResponse?.error && onErrorAddPayment) {
          onErrorAddPayment();
        }
      }
    }

    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} {...props}>
      {topActionButtonElement}
      <div className={className}>
        <div className="flex items-start gap-2">
          {cardIconsList.map(({ brandName }) => (
            <CardIcon className="h-[72px] w-[120px] md:h-[32px] md:w-[53px]" key={brandName} brandName={brandName} />
          ))}
        </div>
        <div className="relative mt-4">
          <label
            htmlFor="cardNumber"
            className="font-albert-sans text-label-gray mb-1 block text-sm font-medium leading-5"
          >
            Card Number <span className="text-red-500">*</span>
          </label>
          <CardNumberElement id="cardNumber" options={ELEMENT_OPTIONS} />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="relative">
            <label
              htmlFor="expiry"
              className="font-albert-sans text-label-gray mb-1 block text-sm font-medium leading-5"
            >
              Card Expiration <span className="text-red-500">*</span>
            </label>
            <CardExpiryElement id="expiry" options={ELEMENT_OPTIONS} />
          </div>
          <div className="relative">
            <label htmlFor="cvc" className="font-albert-sans text-label-gray mb-1 block text-sm font-medium leading-5">
              CVC <span className="text-red-500">*</span>
            </label>
            <CardCvcElement id="cvc" options={ELEMENT_OPTIONS} />
          </div>
        </div>
      </div>
      {bottomActionButtonElement}
    </form>
  );
};

export { AddPaymentFormWithStripe as AddPaymentForm };
